import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"

import { cn } from "@/lib/utils"

/**
 * Avatar component displays a user's profile picture or initials.
 * Built on Radix UI Avatar primitives with customizable styling.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>} props - Avatar root props
 * @returns {JSX.Element} Avatar root container
 * 
 * @example
 * ```tsx
 * <Avatar>
 *   <AvatarImage src="/user.jpg" alt="User" />
 *   <AvatarFallback>JD</AvatarFallback>
 * </Avatar>
 * ```
 */
const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

/**
 * AvatarImage component displays the user's profile image within an Avatar.
 * Falls back to AvatarFallback if image fails to load.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>} props - Avatar image props
 * @param {string} props.src - Source URL of the image
 * @param {string} props.alt - Alt text for the image
 * @returns {JSX.Element} Avatar image element
 */
const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
))
AvatarImage.displayName = AvatarPrimitive.Image.displayName

/**
 * AvatarFallback component displays fallback content (typically user initials)
 * when the AvatarImage fails to load or is not provided.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>} props - Avatar fallback props
 * @param {React.ReactNode} props.children - Fallback content to display
 * @returns {JSX.Element} Avatar fallback element
 */
const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    )}
    {...props}
  />
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
